// AddChildCategoryDialogContent.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import AtomButton from '@/components/atoms/Button';
import { AppDispatch } from '@/store';
import { selectUser } from '@/store/auth/selectors';
import {
  selectCategoriesList,
  selectCategoriesLoading,
  selectCategoriesError,
} from '@/store/categories/selectors';
import { categories } from '@/store/categories/thunks';
import { studyYearCategories } from '@/store/study-year-categories';
import { AddChildCategoryRequest } from '@/types/study-year-category';

interface AddChildCategoryDialogProps {
  parentNode: any;
  parentCategoryId: string;
  onClose: () => void;
  onSuccess: (parentId: string) => void;
}

const AddChildCategoryDialogContent: React.FC<AddChildCategoryDialogProps> = ({
  parentNode,
  parentCategoryId,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const categoriesList = useSelector(selectCategoriesList);
  const isLoadingCategories = useSelector(selectCategoriesLoading);
  const categoriesError = useSelector(selectCategoriesError);
  const useUser = useSelector(selectUser);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedCategory, setSelectedCategory] = useState<any>(null);

  useEffect(() => {
    if (parentCategoryId) {
      console.log(parentCategoryId);
      fetchCategories();
    }
  }, [parentCategoryId]);

  const fetchCategories = async () => {
    try {
      await dispatch(
        categories.getCategories({ is_segment__eq: false, parent_id__eq: parentCategoryId })
      ); // false for child categories
    } catch (error) {
      console.error('Error fetching categories:', error);
      setErrors({ categories: 'Failed to load categories' });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!selectedCategory) {
      // Changed from selectedCategoryId
      newErrors.categoryId = 'Category selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const requestData: AddChildCategoryRequest = {
        studyYearCategoryId: parentNode?.id || parentNode?.study_year_category_id,
        user_id: useUser?.id || useUser?.user_id || '',
        category_id: selectedCategory.id,
      };

      console.log('Adding child category with data:', requestData);

      await dispatch(studyYearCategories.addChildCategory(requestData));

      // Call success callback to refresh parent data
      onSuccess(parentNode?.id || parentNode?.study_year_category_id);
      onClose();
    } catch (error) {
      console.error('Error adding child category:', error);
      setErrors({ submit: 'Failed to add category. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedCategory(null);
    setErrors({});
    onClose();
  };

  return (
    <Box sx={{ minWidth: 400 }}>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Adding child category to: <strong>{parentNode?.category_name || parentNode?.name}</strong>
      </Typography>

      <Divider sx={{ mb: 3 }} />

      <Stack spacing={3}>
        <Autocomplete
          value={selectedCategory}
          onChange={(event, newValue) => setSelectedCategory(newValue)}
          options={categoriesList.filter(category => category.is_segment === false)}
          getOptionLabel={option => option.name || option.category_name || 'Unnamed Category'}
          // Add this to ensure unique keys:
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          loading={isLoadingCategories}
          disabled={isLoadingCategories}
          renderInput={params => (
            <TextField
              {...params}
              label="Select Child Category"
              error={!!errors.categoryId}
              helperText={errors.categoryId}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {isLoadingCategories ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id || option.category_id}>
              {' '}
              {/* Add explicit key */}
              <div>
                <Typography variant="body2">
                  {option.name || option.category_name || 'Unnamed Category'}
                </Typography>
                {option.description && (
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                )}
              </div>
            </Box>
          )}
          noOptionsText={isLoadingCategories ? 'Loading categories...' : 'No categories available'}
        />

        {categoriesError && (
          <Typography color="error" variant="body2">
            {categoriesError}
          </Typography>
        )}

        {errors.submit && (
          <Typography color="error" variant="body2">
            {errors.submit}
          </Typography>
        )}
      </Stack>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <AtomButton variant="outlined" onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </AtomButton>
        <AtomButton
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedCategory || isLoadingCategories}
        >
          {isSubmitting ? 'Adding...' : 'Add Category'}
        </AtomButton>
      </Box>
    </Box>
  );
};

export default AddChildCategoryDialogContent;
